import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import { apiClient } from '@/services/api/client';
import { doc, getDoc } from 'firebase/firestore';

interface User {
  id: string;
  uid: string;   // ← ajouter cette ligne
  email: string | null;
  displayName: string | null;
  photoURL?: string | null;
  roles?: string[];
  emailVerified: boolean;
}

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  register: (email: string, password: string, displayName?: string, roles?: string[]) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  firebaseUser: null,
  loading: true,
  register: async () => {},
  login: async () => {},
  loginWithGoogle: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Extract roles from Firebase ID token claims
  const getRolesFromToken = async (fbUser: FirebaseUser): Promise<string[]> => {
    try {
      const tokenResult = await fbUser.getIdTokenResult();
      const claims = tokenResult.claims;
      // Support both 'roles' array claim and individual boolean claims
      if (Array.isArray(claims.roles)) {
        return claims.roles as string[];
      }
      // Fallback: check individual boolean claims
      const knownRoles = ['admin', 'staff', 'buyer', 'seller', 'broker', 'consultant', 'lender', 'partner', 'internalPartner'];
      return knownRoles.filter(role => claims[role] === true);
    } catch {
      return [];
    }
  };

  // Charger le profil : Firebase claims d'abord (instantané), puis enrichir via backend si disponible
  const loadUserProfile = async (fbUser: FirebaseUser) => {
    let tokenRoles = await getRolesFromToken(fbUser);

    let profilePhotoURL: string | null = fbUser.photoURL;
    let profileDisplayName: string | null = fbUser.displayName;

    if (tokenRoles.length === 0) {
      try {
        const userSnap = await getDoc(doc(db, 'users', fbUser.uid));
        const firestoreRoles = userSnap.exists() ? userSnap.data().roles : undefined;
        if (Array.isArray(firestoreRoles)) {
          tokenRoles = firestoreRoles.filter((role): role is string => typeof role === 'string');
        }
      } catch (error) {
        console.warn('Unable to load Firestore roles fallback:', error instanceof Error ? error.message : error);
      }
    }

    try {
      const profileSnap = await getDoc(doc(db, 'userProfiles', fbUser.uid));
      if (profileSnap.exists()) {
        const profileData = profileSnap.data();
        profilePhotoURL = profileData.photoURL || profilePhotoURL || null;
        profileDisplayName = profileData.displayName || profileDisplayName;
      }
    } catch (error) {
      console.warn('Unable to load Firestore profile, using Firebase profile only:', error instanceof Error ? error.message : error);
    }

    // Immédiatement disponible via Firebase — pas de dépendance au backend
    const firebaseUser: User = {
      id: fbUser.uid,
      uid: fbUser.uid,
      email: fbUser.email,
      displayName: profileDisplayName,
      photoURL: profilePhotoURL,
      roles: tokenRoles,
      emailVerified: fbUser.emailVerified,
    };
    setUser(firebaseUser);

    // Tenter d'enrichir avec le backend NestJS (non-bloquant, timeout court)
    try {
      const token = await fbUser.getIdToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s max

      const response = await apiClient.get('/auth/profile', {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      setUser({
        ...firebaseUser,
        uid: fbUser.uid,
        displayName: response.data.displayName || firebaseUser.displayName,
        photoURL: response.data.photoURL || firebaseUser.photoURL,
        roles: response.data.roles?.length ? response.data.roles : tokenRoles,
      });
    } catch (error) {
      // Backend indisponible — on reste sur les données Firebase (déjà settées)
      console.warn('Backend unreachable, using Firebase claims only:', error instanceof Error ? error.message : error);
    }
  };

  // Écouter les changements d'authentification Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        await loadUserProfile(fbUser);
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Register
  const register = async (email: string, password: string, displayName?: string, roles?: string[]) => {
    try {
      // 1. Créer l'utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // 2. Obtenir le token
      const idToken = await userCredential.user.getIdToken();

      // Only auto-assign buyer role if explicitly registering as buyer
      const rolesToAssign = roles?.length ? roles : ['buyer'];
for (const role of rolesToAssign) {
  try {
    await apiClient.post('/auth/assign-role',
      { uid: userCredential.user.uid, role },
      { headers: { Authorization: `Bearer ${idToken}` } }
    );
  } catch (e) {
    console.warn(`Role assignment failed for ${role}:`, e);
  }
}    

      // 3. Créer le profil dans Firestore via backend
      await apiClient.post('/auth/register', {
        displayName: displayName || email.split('@')[0],
        roles: roles || ['buyer'],
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      // 4. Le profil sera chargé automatiquement par onAuthStateChanged
    } catch (error: any) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Login
  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged sera déclenché automatiquement
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Google Login
  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // Créer/mettre à jour le profil
      const idToken = await userCredential.user.getIdToken();

      try {
  await apiClient.post('/auth/assign-role',
    { uid: userCredential.user.uid, role: 'buyer' },
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
} catch (e) {
  console.warn('Role assignment failed:', e);
}
      await apiClient.post('/auth/register', {
        displayName: userCredential.user.displayName,
        roles: ['buyer'],
      }, {
        headers: { Authorization: `Bearer ${idToken}` }
      });
    } catch (error: any) {
      console.error('Google login error:', error);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Refresh (recharger le profil)
  const refresh = async () => {
    if (firebaseUser) {
      await loadUserProfile(firebaseUser);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      firebaseUser,
      loading,
      register,
      login,
      loginWithGoogle,
      logout,
      refresh
    }}>
      {children}
    </AuthContext.Provider>
  );
};
